import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertColor } from '@mui/material/Alert';
import { registerLearningLabToastBridge } from '../utils/learningLabToast';
import { registerNotificationToastBridge } from '../utils/notificationToast';
import {
  getNotificationReactContext,
  type NotificationContextType,
} from './notificationReactContext';

const NotificationContext = getNotificationReactContext();

const noopNotification: NotificationContextType = {
  showNotification: () => undefined,
  showSuccess: () => undefined,
  showError: () => undefined,
  showWarning: () => undefined,
  showInfo: () => undefined,
};

interface NotificationState {
  open: boolean;
  message: string;
  severity: AlertColor;
  duration: number;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'info',
    duration: 6000,
  });

  const showNotification = useCallback((message: string, severity: AlertColor = 'info', duration: number = 6000) => {
    setNotification({
      open: true,
      message,
      severity,
      duration,
    });
  }, []);

  const showSuccess = useCallback((message: string, duration: number = 6000) => {
    showNotification(message, 'success', duration);
  }, [showNotification]);

  const showError = useCallback((message: string, duration: number = 6000) => {
    showNotification(message, 'error', duration);
  }, [showNotification]);

  const showWarning = useCallback((message: string, duration: number = 6000) => {
    showNotification(message, 'warning', duration);
  }, [showNotification]);

  const showInfo = useCallback((message: string, duration: number = 6000) => {
    showNotification(message, 'info', duration);
  }, [showNotification]);

  useEffect(() => {
    registerLearningLabToastBridge(
      (message) => showInfo(message),
      (message) => showError(message),
    );
    registerNotificationToastBridge({
      showSuccess: (message, durationMs) => showSuccess(message, durationMs ?? 6000),
      showError: (message, durationMs) => showError(message, durationMs ?? 6000),
      showWarning: (message, durationMs) => showWarning(message, durationMs ?? 6000),
      showInfo: (message, durationMs) => showInfo(message, durationMs ?? 6000),
    });
  }, [showError, showInfo, showSuccess, showWarning]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  const value = useMemo(
    () => ({
      showNotification,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [showError, showInfo, showNotification, showSuccess, showWarning],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            maxWidth: 520,
            boxShadow: 3,
            whiteSpace: 'normal',
            alignItems: 'flex-start',
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    return noopNotification;
  }
  return context;
};

/** Alias for components that expect the older helper hook name. */
export const useNotificationHelpers = useNotification;

export type { NotificationContextType };
